import React from 'react';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  SoaBandKey, SoaBorderSide, SoaColumn, SoaElement, SoaFieldDef, SoaStyle, SoaTemplateDefinition,
  SOA_BAND_LABELS, soaContentWidthMm,
} from '../../types/formattedSoa';
import {
  BAND_SHORT, DND_FIELD, DND_ROW_FIELD, ELEMENT_LABELS, SoaDropTarget, SoaSelection, canvasFont, clamp,
  fieldPreview, fitBandToElements, formatDatePattern, formatSampleAmount, mergeStyle, ptToPx, round2, snapTo,
  updateColumn, updateElement,
} from '../../utils/soaDesigner';

/**
 * The Statement Templates design surface: the bands in print order (page header, report header, the
 * transaction table, report footer, page footer) at the template's content width, in mm × zoom.
 * Elements are moved by dragging (Shift keeps the axis) and resized by their eight handles; band heights
 * and column widths by their edges. Fields dragged from the field list are dropped onto a band (an
 * element) or the table (a column). The printed result is in the Preview panel.
 */

type Change = (fn: (d: SoaTemplateDefinition) => SoaTemplateDefinition, mergeKey?: string) => void;

interface CanvasProps {
  def: SoaTemplateDefinition;
  zoom: number;
  selection: SoaSelection;
  onSelect: (s: SoaSelection) => void;
  onChange: Change;
  fields: Map<string, SoaFieldDef>;
  snap: number | null;
  showGrid: boolean;
  onDropField: (target: SoaDropTarget, key: string) => void;
}

type Handle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const HANDLE_CURSOR: Record<Handle, string> = {
  n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize', ne: 'nesw-resize', sw: 'nesw-resize', nw: 'nwse-resize', se: 'nwse-resize',
};

const SELECT_BLUE = '#1976d2';

/** Follows the pointer until it is released (dx / dy in px from the start). */
function trackPointer(e: React.PointerEvent, onMove: (dx: number, dy: number, ev: PointerEvent) => void): void {
  const sx = e.clientX;
  const sy = e.clientY;
  const move = (ev: PointerEvent) => onMove(ev.clientX - sx, ev.clientY - sy, ev);
  const up = () => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    window.removeEventListener('pointercancel', up);
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
  window.addEventListener('pointercancel', up);
}

function textCss(s: SoaStyle, def: SoaTemplateDefinition, zoom: number): React.CSSProperties {
  return {
    fontFamily: canvasFont(s.fontFamily, def.defaults.fontFamily),
    fontSize: ptToPx(s.fontSize ?? def.defaults.fontSize, zoom),
    fontWeight: s.bold ? 700 : 400,
    fontStyle: s.italic ? 'italic' : 'normal',
    textDecoration: s.underline ? 'underline' : 'none',
    textTransform: s.uppercase ? 'uppercase' : 'none',
    color: s.color ?? def.defaults.color,
    lineHeight: s.lineHeight ?? def.defaults.lineHeight,
    letterSpacing: s.letterSpacing ? ptToPx(s.letterSpacing, zoom) : undefined,
    textAlign: s.align ?? 'left',
    direction: s.direction && s.direction !== 'auto' ? s.direction : undefined,
  };
}

const sideCss = (b: SoaBorderSide | undefined, zoom: number): string | undefined =>
  (b && b.width > 0 ? `${Math.max(1, ptToPx(b.width, zoom))}px ${b.style} ${b.color}` : undefined);

function boxCss(s: SoaStyle, zoom: number): React.CSSProperties {
  return {
    backgroundColor: s.background || undefined,
    padding: s.padding !== undefined ? s.padding * zoom : undefined,
    borderTop: sideCss(s.borders?.top, zoom),
    borderRight: sideCss(s.borders?.right, zoom),
    borderBottom: sideCss(s.borders?.bottom, zoom),
    borderLeft: sideCss(s.borders?.left, zoom),
  };
}

const VALIGN: Record<string, string> = { top: 'flex-start', middle: 'center', bottom: 'flex-end' };

/** Text with its {{placeholders}} highlighted. */
function withPlaceholders(text: string): React.ReactNode[] {
  return text.split(/(\{\{[^}]*\}\})/g).map((part, i) => (
    /^\{\{.*\}\}$/.test(part)
      ? <span key={i} style={{ background: 'rgba(25,118,210,0.13)', borderRadius: 2 }}>{part}</span>
      : <React.Fragment key={i}>{part}</React.Fragment>
  ));
}

// ── one element ──────────────────────────────────────────────────────────────────────────────

const ElementContent: React.FC<{ el: SoaElement; def: SoaTemplateDefinition; zoom: number; fields: Map<string, SoaFieldDef> }> = ({ el, def, zoom, fields }) => {
  const s = el.style ?? {};
  const fill: React.CSSProperties = {
    position: 'absolute', inset: 0, overflow: 'hidden', boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
    justifyContent: VALIGN[s.valign ?? 'top'],
  };
  const flow: React.CSSProperties = { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' };
  switch (el.type) {
    case 'text':
      return <div style={{ ...fill, ...textCss(s, def, zoom), ...boxCss(s, zoom) }}><span style={flow}>{withPlaceholders(el.text || ' ')}</span></div>;
    case 'field':
      return (
        <div style={{ ...fill, ...textCss(s, def, zoom), ...boxCss(s, zoom) }}>
          <span style={flow}>
            {el.prefix}
            <span style={{ background: 'rgba(25,118,210,0.13)', borderRadius: 2 }}>{fieldPreview(fields, el.field)}</span>
            {el.suffix}
          </span>
        </div>
      );
    case 'html':
      return (
        <div style={{ ...fill, ...boxCss(s, zoom), fontFamily: 'monospace', fontSize: Math.max(9, ptToPx(7, zoom)), color: '#6a3d9a', background: s.background || 'rgba(106,61,154,0.05)' }}>
          <span style={flow}>{el.html || '<html>'}</span>
        </div>
      );
    case 'image':
      return el.source === 'custom' && el.dataUrl ? (
        <div style={{ ...fill, ...boxCss(s, zoom) }}>
          <img src={el.dataUrl} alt="Custom" style={{ width: '100%', height: '100%', objectFit: el.fit ?? 'contain', display: 'block' }} />
        </div>
      ) : (
        <div style={{ ...fill, ...boxCss(s, zoom), alignItems: 'center', justifyContent: 'center', border: '1px dashed #90a4ae', color: '#607d8b', fontSize: 11, background: 'repeating-linear-gradient(45deg, #fafafa, #fafafa 6px, #f0f3f5 6px, #f0f3f5 12px)' }}>
          {el.source === 'custom' ? 'IMAGE (none chosen)' : 'COMPANY LOGO'}
        </div>
      );
    case 'line': {
      const line = `${Math.max(1, ptToPx(el.thickness, zoom))}px ${el.lineStyle} ${el.color}`;
      return el.orientation === 'vertical'
        ? <div style={{ position: 'absolute', left: 3, top: 0, bottom: 0, borderLeft: line }} />
        : <div style={{ position: 'absolute', left: 0, right: 0, top: 3, borderTop: line }} />;
    }
    case 'box':
      return <div style={{ ...fill, ...boxCss(s, zoom), borderRadius: (el.radius ?? 0) * zoom }} />;
    case 'kv': {
      const grid = el.gridWidth > 0 ? `${Math.max(1, ptToPx(el.gridWidth, zoom))}px solid ${el.gridColor}` : 'none';
      const ls = mergeStyle(s, el.labelStyle);
      const vs = mergeStyle(s, el.valueStyle);
      return (
        <div style={{ ...fill, ...boxCss(s, zoom), padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', border: grid }}>
            <tbody>
              {el.rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ width: `${el.labelWidth}%`, border: grid, ...textCss(ls, def, zoom), ...boxCss(ls, zoom), ...flow }}>{withPlaceholders(r.label)}</td>
                  <td style={{ border: grid, ...textCss(vs, def, zoom), ...boxCss(vs, zoom), ...flow }}>{withPlaceholders(r.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    default:
      return null;
  }
};

interface ElementBoxProps {
  el: SoaElement;
  band: SoaBandKey;
  def: SoaTemplateDefinition;
  zoom: number;
  snap: number | null;
  contentW: number;
  selected: boolean;
  fields: Map<string, SoaFieldDef>;
  onSelect: (s: SoaSelection) => void;
  onChange: Change;
}

const ElementBox: React.FC<ElementBoxProps> = ({ el, band, def, zoom, snap, contentW, selected, fields, onSelect, onChange }) => {
  const isLine = el.type === 'line';
  const vertical = isLine && el.orientation === 'vertical';
  const horizontal = isLine && !vertical;
  const left = el.x * zoom;
  const top = el.y * zoom - (isLine ? 3 : 0);
  const width = vertical ? 8 : el.w * zoom;
  const height = horizontal ? 8 : el.h * zoom;

  const select = () => onSelect({ kind: 'element', band, id: el.id });

  const startMove = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    select();
    const o = { x: el.x, y: el.y };
    const key = `move-${el.id}-${Date.now()}`;
    let started = false;
    trackPointer(e, (dxPx, dyPx, ev) => {
      if (!started && Math.abs(dxPx) < 3 && Math.abs(dyPx) < 3) return;
      started = true;
      let dx = dxPx / zoom;
      let dy = dyPx / zoom;
      if (ev.shiftKey) {
        if (Math.abs(dx) > Math.abs(dy)) dy = 0;
        else dx = 0;
      }
      const x = clamp(snapTo(o.x + dx, snap), -el.w + 2, contentW - 1);
      const y = Math.max(0, snapTo(o.y + dy, snap));
      onChange((d) => fitBandToElements(updateElement(d, band, el.id, (x0) => ({ ...x0, x, y })), band), key);
    });
  };

  const startResize = (e: React.PointerEvent, h: Handle) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const o = { x: el.x, y: el.y, w: el.w, h: el.h };
    const key = `size-${el.id}-${Date.now()}`;
    const minW = 1;
    const minH = isLine ? 0.2 : 1;
    trackPointer(e, (dxPx, dyPx) => {
      const dx = dxPx / zoom;
      const dy = dyPx / zoom;
      let { x, y, w, h: hh } = o;
      if (h.includes('e')) w = o.w + dx;
      if (h.includes('s')) hh = o.h + dy;
      if (h.includes('w')) { x = o.x + dx; w = o.w - dx; }
      if (h.includes('n')) { y = o.y + dy; hh = o.h - dy; }
      x = snapTo(x, snap);
      y = snapTo(y, snap);
      w = snapTo(w, snap);
      hh = snapTo(hh, snap);
      if (w < minW) { if (h.includes('w')) x = o.x + o.w - minW; w = minW; }
      if (hh < minH) { if (h.includes('n')) y = o.y + o.h - minH; hh = minH; }
      x = Math.max(-o.w + 2, x);
      y = Math.max(0, y);
      onChange((d) => fitBandToElements(updateElement(d, band, el.id, (e0) => ({ ...e0, x: round2(x), y: round2(y), w: round2(w), h: round2(hh) })), band), key);
    });
  };

  const handles: Handle[] = horizontal ? ['w', 'e'] : vertical ? ['n', 's'] : ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
  const handlePos = (h: Handle): React.CSSProperties => {
    const p: React.CSSProperties = {};
    if (h.includes('n')) p.top = -5; else if (h.includes('s')) p.bottom = -5; else p.top = 'calc(50% - 4px)';
    if (h.includes('w')) p.left = -5; else if (h.includes('e')) p.right = -5; else p.left = 'calc(50% - 4px)';
    return p;
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`${ELEMENT_LABELS[el.type]}${el.name ? `: ${el.name}` : ''} in ${BAND_SHORT[band]}`}
      aria-pressed={selected}
      data-soa-element={el.id}
      onPointerDown={startMove}
      onFocus={() => { if (!selected) select(); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(); } }}
      sx={{
        position: 'absolute', left, top, width, height, cursor: 'move', userSelect: 'none', touchAction: 'none',
        outline: selected ? `1.5px solid ${SELECT_BLUE}` : '1px dotted rgba(0,0,0,0.22)',
        outlineOffset: 0, zIndex: selected ? 3 : 1,
        '&:hover': { outline: selected ? `1.5px solid ${SELECT_BLUE}` : '1px solid rgba(25,118,210,0.6)' },
        '&:focus-visible': { outline: `2px solid ${SELECT_BLUE}` },
      }}
    >
      <ElementContent el={el} def={def} zoom={zoom} fields={fields} />
      {el.canGrow && (el.type === 'text' || el.type === 'field' || el.type === 'kv' || el.type === 'html') && (
        <Box aria-hidden sx={{ position: 'absolute', right: 1, bottom: -1, fontSize: 9, lineHeight: 1, color: 'rgba(25,118,210,0.7)', pointerEvents: 'none' }}>↧</Box>
      )}
      {selected && handles.map((h) => (
        <Box
          key={h}
          aria-hidden
          onPointerDown={(e) => startResize(e, h)}
          sx={{
            position: 'absolute', width: 8, height: 8, bgcolor: '#fff', border: `1.5px solid ${SELECT_BLUE}`,
            borderRadius: '1px', cursor: HANDLE_CURSOR[h], zIndex: 4, touchAction: 'none', ...handlePos(h),
          }}
        />
      ))}
    </Box>
  );
};

// ── bands ────────────────────────────────────────────────────────────────────────────────────

function gridBackground(zoom: number, on: boolean): Record<string, string> {
  if (!on) return {};
  const step = 5 * zoom;
  return {
    backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.045) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.045) 1px, transparent 1px)',
    backgroundSize: `${step}px ${step}px`,
  };
}

const BandCaption: React.FC<{
  label: string; detail: string; selected: boolean; visible?: boolean; onSelect: () => void; onToggle?: () => void;
}> = ({ label, detail, selected, visible, onSelect, onToggle }) => (
  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1.25, mb: 0.25 }}>
    <Button
      size="small"
      onClick={onSelect}
      sx={{ textTransform: 'none', py: 0, px: 0.75, minWidth: 0, fontWeight: selected ? 700 : 500, color: selected ? SELECT_BLUE : 'text.secondary' }}
    >
      {label}
    </Button>
    <Typography variant="caption" color="text.secondary">{detail}</Typography>
    {onToggle && (
      <Tooltip title={visible ? 'Printed: click to leave this band out' : 'Not printed: click to print this band'}>
        <IconButton size="small" aria-label={visible ? `Hide ${label}` : `Show ${label}`} onClick={onToggle}>
          {visible ? <VisibilityIcon fontSize="inherit" /> : <VisibilityOffIcon fontSize="inherit" />}
        </IconButton>
      </Tooltip>
    )}
  </Stack>
);

interface BandViewProps extends Omit<CanvasProps, 'selection'> {
  band: SoaBandKey;
  selection: SoaSelection;
  contentW: number;
}

const BandView: React.FC<BandViewProps> = ({
  band, def, zoom, selection, onSelect, onChange, fields, snap, showGrid, contentW, onDropField,
}) => {
  const b = def.bands[band];
  const bandSelected = selection?.kind === 'band' && selection.band === band;
  const W = contentW * zoom;

  const startHeightDrag = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const h0 = b.height;
    const key = `bandh-${band}-${Date.now()}`;
    trackPointer(e, (_dx, dyPx) => {
      const height = Math.max(1, snapTo(h0 + dyPx / zoom, snap));
      onChange((d) => ({ ...d, bands: { ...d.bands, [band]: { ...d.bands[band], height } } }), key);
    });
  };

  const nudgeHeight = (delta: number) => onChange(
    (d) => ({ ...d, bands: { ...d.bands, [band]: { ...d.bands[band], height: Math.max(1, round2(d.bands[band].height + delta)) } } }),
    `bandh-${band}-keys`,
  );

  const onDrop = (e: React.DragEvent) => {
    const key = e.dataTransfer.getData(DND_FIELD);
    if (!key) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    onDropField({ kind: 'band', band, x: snapTo((e.clientX - rect.left) / zoom, snap), y: snapTo((e.clientY - rect.top) / zoom, snap) }, key);
  };

  return (
    <Box>
      <BandCaption
        label={SOA_BAND_LABELS[band]}
        detail={`${b.height} mm${b.visible ? '' : ' · not printed'}`}
        selected={bandSelected}
        visible={b.visible}
        onSelect={() => onSelect({ kind: 'band', band })}
        onToggle={() => onChange((d) => ({ ...d, bands: { ...d.bands, [band]: { ...d.bands[band], visible: !d.bands[band].visible } } }))}
      />
      <Box
        data-soa-band={band}
        onPointerDown={(e) => { if (e.target === e.currentTarget) onSelect({ kind: 'band', band }); }}
        onDragOver={(e) => { if (e.dataTransfer.types.includes(DND_FIELD)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } }}
        onDrop={onDrop}
        sx={{
          position: 'relative', width: W, height: b.height * zoom, bgcolor: b.background || '#ffffff',
          opacity: b.visible ? 1 : 0.45,
          outline: bandSelected ? `2px solid ${SELECT_BLUE}` : '1px dashed #9aa5b1',
          ...gridBackground(zoom, showGrid),
        }}
      >
        {b.elements.map((el) => (
          <ElementBox
            key={el.id}
            el={el}
            band={band}
            def={def}
            zoom={zoom}
            snap={snap}
            contentW={contentW}
            fields={fields}
            selected={selection?.kind === 'element' && selection.band === band && selection.id === el.id}
            onSelect={onSelect}
            onChange={onChange}
          />
        ))}
        <Box
          role="separator"
          aria-orientation="horizontal"
          aria-label={`${SOA_BAND_LABELS[band]} height ${b.height} mm`}
          aria-valuenow={b.height}
          tabIndex={0}
          onPointerDown={startHeightDrag}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp') { e.preventDefault(); nudgeHeight(e.shiftKey ? -5 : -1); }
            if (e.key === 'ArrowDown') { e.preventDefault(); nudgeHeight(e.shiftKey ? 5 : 1); }
          }}
          sx={{
            position: 'absolute', left: 0, right: 0, bottom: -4, height: 8, cursor: 'ns-resize', zIndex: 5, touchAction: 'none',
            '&:hover, &:focus-visible': { bgcolor: 'rgba(25,118,210,0.25)', outline: 'none' },
          }}
        />
      </Box>
    </Box>
  );
};

// ── the transaction table ────────────────────────────────────────────────────────────────────

const OPENING = { postingDate: '2025-01-01', balance: 56124.104, net: 56124.104, movement: 56124.104, balanceSide: 'Debit' };
const CLOSING = { postingDate: '2025-12-31', debit: 80597.75, credit: 97008.53, movement: -16410.78, balance: 39713.324, net: 39713.324, balanceSide: 'Debit' };

function cellText(col: SoaColumn, field: SoaFieldDef | undefined, def: SoaTemplateDefinition, row: 'tx' | 'opening' | 'closing', i: number): string {
  if (row === 'opening' && def.table.openingRow.labelColumn === col.id) return def.table.openingRow.label;
  if (row === 'closing' && def.table.closingRow.labelColumn === col.id) return def.table.closingRow.label;
  let v: unknown;
  if (row === 'opening') v = (OPENING as Record<string, unknown>)[col.field];
  else if (row === 'closing') {
    v = (CLOSING as Record<string, unknown>)[col.field];
    if (!def.table.closingRow.showTotals && ['debit', 'credit', 'movement'].includes(col.field)) v = undefined;
  } else if (col.field === 'docType') v = Object.values(def.docTypes)[i % Math.max(1, Object.values(def.docTypes).length)] ?? '13';
  else v = field?.sample;
  if (v === undefined || v === null || v === '') return '';
  const type = field?.type ?? 'text';
  const [fmt, arg] = (col.format ?? '').split(':');
  if (type === 'date' || type === 'datetime') {
    const pattern = fmt === 'date' || fmt === 'datetime' ? (arg || (fmt === 'datetime' ? def.formats.dateTime : def.formats.date)) : def.formats.date;
    return formatDatePattern(String(v), pattern);
  }
  if (type === 'amount') {
    const n = Number(v) * (row === 'tx' ? 1 + i * 0.37 : 1);
    return formatSampleAmount(n, col.format, def);
  }
  if (fmt === 'upper') return String(v).toUpperCase();
  if (fmt === 'lower') return String(v).toLowerCase();
  return String(v);
}

interface TableViewProps extends Omit<CanvasProps, 'selection'> {
  selection: SoaSelection;
  contentW: number;
}

const TableView: React.FC<TableViewProps> = ({ def, zoom, selection, onSelect, onChange, fields, snap, contentW, onDropField }) => {
  const t = def.table;
  const W = contentW * zoom;
  const total = round2(t.columns.reduce((s, c) => s + c.width, 0));
  const tableSelected = selection?.kind === 'table';
  const grid = t.gridWidth > 0 ? `${Math.max(1, ptToPx(t.gridWidth, zoom))}px solid ${t.gridColor}` : 'none';

  const startColumnDrag = (e: React.PointerEvent, col: SoaColumn) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const w0 = col.width;
    const key = `colw-${col.id}-${Date.now()}`;
    trackPointer(e, (dxPx) => {
      const width = Math.max(3, snapTo(w0 + dxPx / zoom, snap));
      onChange((d) => updateColumn(d, col.id, (c) => ({ ...c, width })), key);
    });
  };

  const onDrop = (e: React.DragEvent) => {
    const key = e.dataTransfer.getData(DND_ROW_FIELD);
    if (!key) return;
    e.preventDefault();
    const x = (e.clientX - e.currentTarget.getBoundingClientRect().left) / zoom;
    let acc = 0;
    let index = t.columns.length;
    for (let i = 0; i < t.columns.length; i += 1) {
      if (x < acc + t.columns[i].width / 2) { index = i; break; }
      acc += t.columns[i].width;
    }
    onDropField({ kind: 'table', index }, key);
  };

  const rows: Array<{ kind: 'tx' | 'opening' | 'closing'; i: number }> = [
    ...(t.openingRow.show ? [{ kind: 'opening' as const, i: 0 }] : []),
    { kind: 'tx', i: 0 }, { kind: 'tx', i: 1 }, { kind: 'tx', i: 2 },
    ...(t.closingRow.show ? [{ kind: 'closing' as const, i: 0 }] : []),
  ];

  const rowStyle = (kind: 'tx' | 'opening' | 'closing', i: number): SoaStyle => {
    let s: SoaStyle = {};
    if (kind === 'tx' && t.zebra && i % 2 === 1) s.background = t.zebra;
    if (kind === 'opening') s = mergeStyle(s, t.openingRow.style);
    if (kind === 'closing') s = mergeStyle(s, t.closingRow.style);
    return s;
  };

  const cellBox = (s: SoaStyle, header: boolean): React.CSSProperties => ({
    ...textCss(s, def, zoom),
    ...boxCss(s, zoom),
    borderLeft: t.verticalLines ? grid : undefined,
    borderRight: t.verticalLines ? grid : undefined,
    borderTop: t.horizontalLines || header ? grid : undefined,
    borderBottom: t.horizontalLines || header ? grid : undefined,
    boxSizing: 'border-box',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: VALIGN[s.valign ?? 'middle'],
  });

  return (
    <Box>
      <BandCaption
        label="Details table (transactions)"
        detail={`columns ${total} mm of ${round2(contentW)} mm${Math.abs(total - contentW) > 0.5 ? ' — printed scaled to the table width' : ''}`}
        selected={tableSelected}
        onSelect={() => onSelect({ kind: 'table' })}
      />
      <Box
        data-soa-table
        onPointerDown={(e) => { if (e.target === e.currentTarget) onSelect({ kind: 'table' }); }}
        onDragOver={(e) => { if (e.dataTransfer.types.includes(DND_ROW_FIELD)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } }}
        onDrop={onDrop}
        sx={{
          position: 'relative', width: Math.max(W, total * zoom), bgcolor: '#ffffff', pt: `${t.spaceBefore * zoom}px`, pb: `${t.spaceAfter * zoom}px`,
          outline: tableSelected ? `2px solid ${SELECT_BLUE}` : '1px dashed #9aa5b1',
        }}
      >
        <Box
          sx={{
            display: 'grid', gridTemplateColumns: t.columns.map((c) => `${c.width * zoom}px`).join(' '),
            width: total * zoom, border: t.outerBorder ? grid : undefined, boxSizing: 'content-box',
          }}
        >
          {t.columns.map((col) => {
            const hs = mergeStyle(mergeStyle(t.headerStyle, { align: t.headerStyle.align || col.align }), col.headerStyle);
            const selected = selection?.kind === 'column' && selection.id === col.id;
            const selectColumn = () => onSelect({ kind: 'column', id: col.id });
            return (
              <Box
                key={col.id}
                role="button"
                tabIndex={0}
                aria-label={`Column ${col.header || col.field}`}
                aria-pressed={selected}
                onPointerDown={(e) => { if (e.button === 0) { e.stopPropagation(); selectColumn(); } }}
                onFocus={() => { if (!selected) selectColumn(); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectColumn(); } }}
                style={cellBox(hs, true)}
                sx={{
                  position: 'relative', cursor: 'pointer', minHeight: 6 * zoom,
                  outline: selected ? `2px solid ${SELECT_BLUE}` : 'none', outlineOffset: -2, zIndex: selected ? 2 : 1,
                  '&:focus-visible': { outline: `2px solid ${SELECT_BLUE}` },
                }}
              >
                <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'normal' }}>{col.header}</span>
                <Box
                  aria-hidden
                  onPointerDown={(e) => startColumnDrag(e, col)}
                  sx={{ position: 'absolute', top: 0, bottom: 0, right: -3, width: 6, cursor: 'col-resize', zIndex: 3, touchAction: 'none', '&:hover': { bgcolor: 'rgba(25,118,210,0.3)' } }}
                />
              </Box>
            );
          })}
          {rows.map((r, ri) => t.columns.map((col) => {
            const cs = mergeStyle(mergeStyle(mergeStyle(t.cellStyle, { align: col.align }), col.style), rowStyle(r.kind, r.i));
            const selected = selection?.kind === 'column' && selection.id === col.id;
            return (
              <Box
                key={`${ri}-${col.id}`}
                onPointerDown={(e) => { if (e.button === 0) { e.stopPropagation(); onSelect({ kind: 'column', id: col.id }); } }}
                style={{ ...cellBox(cs, false), minHeight: (r.kind === 'tx' && r.i === 1 ? 9 : 6) * zoom }}
                sx={{ cursor: 'pointer', boxShadow: selected ? 'inset 0 0 0 9999px rgba(25,118,210,0.07)' : undefined }}
              >
                <span style={{ whiteSpace: col.wrap ? 'pre-wrap' : 'nowrap', overflowWrap: 'anywhere' }}>{cellText(col, fields.get(col.field), def, r.kind, r.i)}</span>
              </Box>
            );
          }))}
        </Box>
        {Math.abs(total - contentW) > 0.5 && (
          <Box aria-hidden sx={{ position: 'absolute', top: 0, bottom: 0, left: W, borderLeft: '1px dashed #d32f2f', pointerEvents: 'none' }} />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 0.5, pt: 0.5 }}>
          {t.openingRow.show ? 'First row: opening balance. ' : ''}Three sample transactions{t.closingRow.show ? '; last row: period totals and closing balance' : ''}. Drop table fields here to add columns.
        </Typography>
      </Box>
    </Box>
  );
};

// ── ruler and canvas ─────────────────────────────────────────────────────────────────────────

const Ruler: React.FC<{ widthMm: number; zoom: number }> = ({ widthMm, zoom }) => {
  const labelEvery = zoom * 10 >= 34 ? 10 : 20;
  const ticks: React.ReactNode[] = [];
  for (let mm = 0; mm <= widthMm; mm += 5) {
    const major = mm % 10 === 0;
    ticks.push(
      <Box key={mm} aria-hidden sx={{ position: 'absolute', left: mm * zoom, bottom: 0, height: major ? 8 : 4, borderLeft: '1px solid #8a94a0' }} />,
    );
    if (mm % labelEvery === 0) {
      ticks.push(
        <Box key={`l${mm}`} aria-hidden sx={{ position: 'absolute', left: mm * zoom + 2, top: 0, fontSize: 9, color: '#5f6b7a', lineHeight: 1 }}>{mm}</Box>,
      );
    }
  }
  return <Box sx={{ position: 'relative', width: widthMm * zoom, height: 16, borderBottom: '1px solid #8a94a0', mb: 0.5 }}>{ticks}</Box>;
};

const DesignerCanvas: React.FC<CanvasProps> = (props) => {
  const { def, zoom, onSelect } = props;
  const contentW = soaContentWidthMm(def);
  return (
    <Box
      sx={{ minWidth: 'max-content', px: 3, pb: 4, pt: 1 }}
      onPointerDown={(e) => { if (e.target === e.currentTarget) onSelect(null); }}
    >
      <Ruler widthMm={contentW} zoom={zoom} />
      <BandView {...props} band="pageHeader" contentW={contentW} />
      <BandView {...props} band="reportHeader" contentW={contentW} />
      <TableView {...props} contentW={contentW} />
      <BandView {...props} band="reportFooter" contentW={contentW} />
      <BandView {...props} band="pageFooter" contentW={contentW} />
    </Box>
  );
};

export default DesignerCanvas;
